import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ILoginResponse } from 'src/app/shared/constants/account';
import { AccountStatus } from 'src/app/shared/constants/account-admin';
import { AccountService } from 'src/app/shared/services/account.service';
import { APIService } from 'src/app/shared/services/api.service';
import { CacheService } from 'src/app/shared/services/cache.service';
import { JwtService } from 'src/app/shared/services/jwt.service';

import lodash from 'lodash';
import { ReplaySubject } from 'rxjs';

// NOTE: The default distribution injects its CSS as a side effect as part of its bundle.
//       So we don't use the distribution which injects its CSS.
import Swal, { SweetAlertOptions } from 'sweetalert2/dist/sweetalert2.js'


const hasEmptyValues = (fields: string[], obj: any) => {
  return fields
    .map((field) => lodash.get(obj, field))
    .some((value) => typeof (value) === 'undefined' || value === null);
};

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  private userUrl = 'rest-auth/user/';

  public isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();
  public settingsLocation;
  public editUserId;
  public editUserAccountId;
  public editAccountType;

  constructor(
    private apiService: APIService,
    private http: HttpClient,
    private router: Router,
    private jwtService: JwtService,
    private accountService: AccountService,
    private cacheService: CacheService,
    private readonly route: ActivatedRoute,
  ) { }

  navigateTo(path: string) {
    this.router.navigate([path]);
  }

  alert(title: string, text: string, options: SweetAlertOptions = {}) {
    return Swal.fire({ ...options, title, text });
  }

  showSuccess(message: string) {
    return this.alert('Success', message, { icon: 'success' });
  }

  showError(message: string) {
    return this.alert('Error', message, { icon: 'error' });
  }

  async showMessageAndRedirect(title, message, icon, url) {
    const returnUrl = (
      this.route.snapshot.queryParams.returnUrl
      || url
    );

    const result = await Swal.fire({
      title,
      text: message,
      icon,
    });

    if (result) {
      this.router.onSameUrlNavigation = 'reload';
      this.router.navigateByUrl(returnUrl);
    }
  }

  getCurrentUser() {
    const data: ILoginResponse = this.jwtService.decodeToken();
    return data?.user;
  }

  async getCurrentUserDetails() {
    const url = this.apiService.url(this.userUrl);

    const details = await this.http.get(url).toPromise();
    this.cacheService.currentUserDetails = details;

    return details;
  }

  async getCurrentUserDetailsLazy() {
    return !this.cacheService.currentUserDetails ?
      await this.getCurrentUserDetails() :
      this.cacheService.currentUserDetails;
  }

  async getCurrentUserStatus() {
    const details = await this.getCurrentUserDetailsLazy();
    return lodash.get(details, 'status', null);
  }

  async isCurrentUserActive() {
    const status = await this.getCurrentUserStatus();
    return status === AccountStatus.ACTIVE;
  }

  getCurrentAccountType() {
    return this.getCurrentUser()?.account_type;
  }

  getCurrentAccountId() {
    return this.getCurrentUser()?.account;
  }

  getCurrentUserId() {
    return this.getCurrentUser()?.id;
  }

  getCurrentUserEmail() {
    return this.getCurrentUser()?.email;
  }

  async getCurrentUserGroups() {
    const currentUserDetails = await this.getCurrentUserDetailsLazy();
    return currentUserDetails.groups;
  }

  private async verifyPublisherFinanceFields() {
    const accountId = this.getCurrentAccountId();
    const account = await this.accountService
      .getPublisherAccount(accountId)
      .toPromise();

    const requiredFields = [
      'account.billing_address', // Billing Address
      'account.billing_address.line1', // Street and Number
      'account.billing_address.zip_code', // ZIP Code
      'account.billing_address.city', // City
      'account.billing_address.country', // Country
      'account.payment_bank', // Banking
      'account.payment_bank.account_international_number', // IBAN
      'account.payment_bank.bank_international_number', // BIC / SWIFT
      'account.payment_bank.bank_name', // Name of credit institution
      'account.payment_bank.bank_address', // Bank Address
      'account.payment_bank.bank_address.line1', // Street
      'account.payment_bank.bank_address.line2', // Number
      'account.payment_bank.bank_address.city', // City
      'account.payment_bank.bank_address.country', // Country
      'account.payment_bank.bank_address.zip_code', // ZIP Code
    ];

    return !hasEmptyValues(requiredFields, account);
  }

  private async verifyPublisherAccountFields() {
    const user = await this.getCurrentUserDetailsLazy();
    const requiredFields = [
      'account_info.business_type', // Account Type
      'account_info.contact_address.line1', // Street and Number
      'account_info.contact_address.zip_code', // ZIP Code
      'account_info.contact_address.city', // City
      'account_info.contact_address.country', // Country
      'account_info.contact_email', // Email Address
      'account_info.website_url', // Website URL
      'account_info.contact_phone', // Phone number
      'account_info.contact_phone_country_code', // Country Code
    ];
    const businessType = user.account_info.business_type;
    switch (businessType && businessType.toLowerCase()) {
      case 'commercial':
        requiredFields.push(
          'account_info.company_form', // Company Form
          'account_info.company_name', // Company Name
        );
        break;
      case 'private':
        requiredFields.push('first_name', 'last_name');
        break;
      default:
    }

    return !hasEmptyValues(requiredFields, user);
  }

  public async isIncompletePublisherProfile() {
    if (this.getCurrentAccountType().toLowerCase() !== 'publisher') {
      return { account: false, finance: false };
    }

    const account = !(await this.verifyPublisherAccountFields());
    const finance = !(await this.verifyPublisherFinanceFields());
    return { account, finance };
  }
}
